import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import produce from "immer";

import "./UpdateFiles.scss";
import { ErrorMessage, FormHeading, FormInput, FormSelect, FormTextarea, IUserDefinedAPI, Loader, Page, PageHeader, UserDefined } from "components";
import { Button, Modal, Row, Col} from '@cfa/react-components';
import { DynamoColumn, AsyncStatus } from "enums";
import { IDeviceModel, IUpdateFile, IUpdateType } from "interfaces";
import { parseAxiosError, ManufacturersService, UpdateTypesService, UpdateFilesService } from "services";
import { UploadFileModal } from "components/UploadFileModal/UploadFileModal";

interface ICreateUpdateFilePageState {
    loadError?: string;
    loadStatus?: AsyncStatus;
    saveStatus?: AsyncStatus;
    updateFile?: IUpdateFile;
    updateTypes?: IUpdateType[];
    checkedModelIds?: number[];
    deviceModels?: IDeviceModel[];
    deviceModelError?: boolean;
    pageError?: string;
}

type IUpdateFileForm = {
    description: string;
    s3Url: string;
    manufacturerId: string;
    updateTypeId: string;
    keyMatcher: string;
    version: string;
    dynamoColumnId: DynamoColumn;
};

export function CreateUpdateFile() {
    const { t } = useTranslation();
    const form = useForm<IUpdateFileForm>({ mode: "onTouched" });

    const [pageState, setPageState] = useState<ICreateUpdateFilePageState>({
        loadStatus: AsyncStatus.Success,
        saveStatus: AsyncStatus.Initial,
        updateTypes: [],
        checkedModelIds: [],
        deviceModels: [],
        updateFile: {
            id: undefined,
            description: "",
            updateTypeId: undefined,
            createdAt: null,
            version: "",
            userDefined: null,
            s3URL: "",
            s3CheckSum: "",
            deviceModelIds: [],
            dynamoAttributeId: undefined,
            keyMatcher: "",
        },
    });
    const {
        loadError,
        loadStatus,
        saveStatus,
        updateTypes,
        checkedModelIds,
        deviceModels,
        deviceModelError,
        pageError,
    } = pageState;
    const history = useHistory();
    const userDefinedRef = useRef<IUserDefinedAPI>();

    // turn DynamoColumn enum into an object to be mapped to FormSelect
    let dynamoColumns: { id: number; name: string }[] = [];
    for (var n in DynamoColumn) {
        if (typeof DynamoColumn[n] === "number") {
            dynamoColumns.push({ id: Number(DynamoColumn[n]), name: n });
        }
    }

    // at least one device model must be checked
    function validateDeviceModels(): {isValid: boolean} {
        return {
            isValid: pageState.checkedModelIds.length > 0,
        };
    }

    // This method determines which checked devices will be pushed into deviceModelIds 
    function onDeviceToggle(e: ChangeEvent<HTMLInputElement>) {
        let checkedDeviceIds = [...checkedModelIds];
        e.target.checked ?
            checkedDeviceIds.push(Number(e.target.value)) :
            checkedDeviceIds.splice(checkedDeviceIds.indexOf(Number(e.target.value)), 1);
        setPageState(
            produce((draft) => {
                draft.checkedModelIds = checkedDeviceIds;
            })
        );
    }

    // Changing the manufacturer will reset deviceModels, deviceModelIds, and the updateType dropdown
    function onChangeManufacturer(e: ChangeEvent<HTMLSelectElement>) {
        const val = e.target.value;
        const manId = val && parseInt(val, 10);
        const updateTypes = UpdateTypesService.updateTypes.filter((ut) => ut.manufacturerId === manId);
        const deviceModels = ManufacturersService.models.filter((dm) => dm.manufacturerId === manId);

        setPageState(
            produce((draft) => {
                draft.updateTypes = updateTypes;
                draft.deviceModels = deviceModels;
                draft.checkedModelIds = [];
            })
        );
    }

    const [showUploadFileModal, setShowUploadFileModal] = useState(false);
    
    const onCloseUploadModal = () => {
        setShowUploadFileModal(false);
    }

    const onCancel = () => {
        history.push("/updatefiles");
    };

    // set keyMatcher to 'current' when dynamo column is set to 'menu'
    const [keyMatcherDisabled, setKeyMatcherDisabled] = useState(false);

    const onDynamoColumnChanged = (e) => {
        if (e.target.value === "2") {
            form.setValue("keyMatcher", "current");
            form.trigger("keyMatcher");
            setKeyMatcherDisabled(true);
        } else {
            setKeyMatcherDisabled(false);
        }
    };

    // Validates the entire form before sending
    const onSubmit = () => {
        if (saveStatus === AsyncStatus.Loading) return;

        const modelsResult = validateDeviceModels();
        const userDefinedResult = userDefinedRef.current.validateAndGetValue();

        setPageState(produce(draft => {
            draft.deviceModelError = !modelsResult.isValid;
        }));

        form.handleSubmit((formData: IUpdateFileForm) => {
            if (!modelsResult.isValid || !userDefinedResult.isValid) return;

            // Setstate to loading
            setPageState(produce(draft => {
                draft.pageError = '';
                draft.saveStatus = AsyncStatus.Loading;
            }));
            const updateFile: IUpdateFile = {
                description: formData.description,
                deviceModelIds: [...checkedModelIds],
                dynamoAttributeId: Number(formData.dynamoColumnId),
                keyMatcher: formData.keyMatcher,
                s3URL: formData.s3Url,
                updateTypeId: Number(formData.updateTypeId),
                userDefined: userDefinedResult.userDefined,
                version: formData.version,
            };
            UpdateFilesService.postUpdateFile(updateFile)
                .then(() => {
                    setPageState(produce(draft => {
                        draft.pageError = '';
                        draft.saveStatus = AsyncStatus.Success;
                    }))
                    history.push("/updatefiles");
                })
                .catch((err) => {
                    setPageState(
                        produce((draft) => {
                            draft.pageError = parseAxiosError(err);
                            draft.saveStatus = AsyncStatus.Error;
                        })
                    );
                });
        })();
    };

    return (<>
        {loadStatus === AsyncStatus.Error && (
            <ErrorMessage message={loadError} />
        )}
        {loadStatus === AsyncStatus.Loading && <Loader />}
        {loadStatus === AsyncStatus.Success && (<>
            <Page>
                <PageHeader title="New Update File"/>

                <Row>
                    <Col>
                        <FormHeading>Fields</FormHeading>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <FormInput
                            form={form}
                            label="S3 Url"
                            name="s3Url"
                            registerOptions={{ required: true }}
                        />
                    </Col>
                    <Button style={{marginTop: "18px"}} onClick={() => {
                            setShowUploadFileModal(true);
                        }}
                    >
                        Upload File
                    </Button>
                </Row>
                <Row>
                    <Col>
                        <FormTextarea
                            form={form}
                            label="Description"
                            name="description"
                            maxLength={2000}
                            rows={2}
                            registerOptions={{ required: true }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormSelect
                            form={form}
                            getOptionLabel={(m) => m.name}
                            getOptionValue={(m) => m.id}
                            label="Manufacturer"
                            name="manufacturerId"
                            options={ManufacturersService.manufacturers}
                            registerOptions={{ required: true }}
                            onChange={onChangeManufacturer}
                        />
                    </Col>
                    <Col>
                        <FormSelect
                            form={form}
                            getOptionLabel={(ut) => ut.description}
                            getOptionValue={(ut) => ut.id}
                            label="Update Type"
                            name="updateTypeId"
                            options={updateTypes}
                            registerOptions={{ required: true }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col className="formItem_label">
                        Select Device Models:
                    </Col>
                </Row>
                <Row>
                    {deviceModels.map((device) => (
                        <Col key={device.id}>
                            <label>
                                <input
                                    type="checkbox"
                                    onChange={onDeviceToggle}
                                    value={device.id}
                                />
                                {device.name}
                            </label>
                        </Col>
                    ))}
                </Row>
                {deviceModelError && (
                    <Row>
                        <Col className="updateFiles_deviceModelError">
                            Please select at least one device.
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        <FormHeading>Success Criteria</FormHeading>
                        <p>
                            A device is considered to have
                            successfully updated when we detect the
                            following info in dynamo:
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormSelect
                            form={form}
                            getOptionLabel={(dc) => dc.name}
                            getOptionValue={(dc) => dc.id}
                            label="Dynamo Column"
                            name="dynamoColumnId"
                            options={dynamoColumns}
                            registerOptions={{ required: true }}
                            onChange={onDynamoColumnChanged}
                        />
                    </Col>
                    <Col>
                        <FormInput
                            form={form}
                            label="Key Matcher"
                            name="keyMatcher"
                            maxLength={128}
                            registerOptions={{ required: true }}
                            disabled={keyMatcherDisabled}
                        />
                    </Col>
                    <Col>
                        <FormInput
                            form={form}
                            label="Version"
                            name="version"
                            maxLength={128}
                            registerOptions={{ required: true }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormHeading>User Defined</FormHeading>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <UserDefined
                            ref={userDefinedRef}
                        />
                    </Col>
                </Row>
                <div className="formFooter">
                    {pageError && (
                        <ErrorMessage message={pageError} />
                    )}
                    <Button
                        variant="outlined"
                        disabled={saveStatus === AsyncStatus.Loading}
                        onClick={onCancel}
                    >
                        {t("button.cancel")}
                    </Button>
                    <Button
                        style={{marginLeft: "10px"}}
                        loading={saveStatus === AsyncStatus.Loading}
                        disabled={!form.formState.isValid}
                        onClick={onSubmit}
                    >
                        Submit Update File
                    </Button>
                </div>
            </Page>

            <Modal show={showUploadFileModal} onClose={onCloseUploadModal}>
                <UploadFileModal
                    onHide={onCloseUploadModal}
                    onUpload={(uri) => {
                        form.setValue("s3Url", uri);
                        form.trigger("s3Url");
                        onCloseUploadModal();
                    }}/>
            </Modal>
            </>)}
    </>);
}
